import React, { useState, useEffect, useRef } from 'react';
import { Upload, Loader2, Check, AlertTriangle, File, FileText, RefreshCw, FileSpreadsheet, FileCode, ArrowRight, ArrowLeft, Pencil, Info, Bug, CheckCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import { uploadDocumentsWithProgress, rescrapWebsite, getDiscoveryDefaults } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { addCrawlJob } from '../services/crawlStatusService';
import YouTubeVideos from './YouTubeVideos';
import BotPurposeSelector from '../components/BotPurposeSelector';
import { UnifiedProgressUI } from '../components/CrawlProgress/UnifiedProgressUI';
import { BOT_PURPOSE_PROMPTS } from '../utils/bot_prompts_client';
import { InlineCategoryCard } from '../components/common';

interface DocumentUploadProps {
  clientId: string | null;
}

const MAX_PAGE_SELECTION = 1000;

const normalizeBotKey = (value: string | null | undefined): string =>
  (value ?? '')
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-');

const GENERAL_BOT_KEYS = new Set<string>([
  'general',
  'general-assistant',
  'generalassistant',
  'general-knowledge',
  'general-knowledge-bot'
]);

const isGeneralBotKey = (value: string | null | undefined): boolean => GENERAL_BOT_KEYS.has(normalizeBotKey(value));

const shouldFilterByBotCategories = (
  botCategories: string[] | undefined,
  selectedPurpose: string,
  selectedBotType?: string | null
): boolean => {
  if (!botCategories || botCategories.length === 0) {
    return false;
  }

  if (isGeneralBotKey(selectedPurpose) || isGeneralBotKey(selectedBotType)) {
    return false;
  }

  return true;
};

const extractCategoryParts = (name: string): string[] =>
  (name || '')
    .split('>')
    .map(part => part.trim())
    .filter(Boolean);

const getLeafCategoryName = (name: string): string => {
  const parts = extractCategoryParts(name);
  return parts.length ? parts[parts.length - 1] : (name || 'Uncategorized');
};

type AggregatedCategory = {
  name: string;
  count: number;
  icon?: string;
  rawNames: string[];
  listingCount?: number;
  listingChildrenCount?: number;
  sampleProductUrls?: string[];
};

const aggregateCategoryDetails = (categories: any[]): AggregatedCategory[] => {
  const map = new Map<string, AggregatedCategory>();

  categories.forEach(cat => {
    const displayName = getLeafCategoryName(cat?.name);
    const existing = map.get(displayName);
    const detailCount =
      typeof cat?.detail_count === 'number'
        ? cat.detail_count
        : typeof cat?.count === 'number'
          ? cat.count
          : 0;
    const listingCount =
      typeof cat?.listing_count === 'number' ? cat.listing_count : 0;
    const listingChildrenCount =
      typeof cat?.listing_children_count === 'number' ? cat.listing_children_count : 0;
    const sampleProducts: string[] = Array.isArray(cat?.sample_product_urls)
      ? cat.sample_product_urls.filter((url: unknown): url is string => typeof url === 'string')
      : [];

    if (existing) {
      existing.count += detailCount;
      existing.listingCount = (existing.listingCount || 0) + listingCount;
      existing.listingChildrenCount =
        (existing.listingChildrenCount || 0) + listingChildrenCount;
      if (cat?.icon && !existing.icon) {
        existing.icon = cat.icon;
      }
      if (cat?.name && !existing.rawNames.includes(cat.name)) {
        existing.rawNames.push(cat.name);
      }
      if (sampleProducts.length) {
        const seen = new Set(existing.sampleProductUrls || []);
        sampleProducts.forEach(url => {
          if (!seen.has(url)) {
            seen.add(url);
            existing.sampleProductUrls = [...(existing.sampleProductUrls || []), url];
          }
        });
      }
    } else {
      map.set(displayName, {
        name: displayName,
        count: detailCount,
        icon: cat?.icon,
        rawNames: cat?.name ? [cat.name] : [],
        listingCount: listingCount || undefined,
        listingChildrenCount: listingChildrenCount || undefined,
        sampleProductUrls: sampleProducts.length ? [...sampleProducts] : undefined
      });
    }
  });

  return Array.from(map.values())
    .map(cat => ({
      ...cat,
      icon: cat.icon || '📄'
    }))
    .sort((a, b) => b.count - a.count);
};

interface FileProgress {
  id: string;
  name: string;
  progress: number;
  status: 'pending' | 'uploading' | 'processing' | 'completed' | 'failed';
  fileType: string;
  message?: string;
  size: number;
  processingStage?: string;
  embeddingProgress?: number;
  // Live per-page telemetry from SSE
  pageCurrent?: number;
  pageTotal?: number;
  pageMode?: string; // text | ocr | textract
  pageMethod?: string; // pymupdf | pypdf2 | tesseract | textract
  pageConfidence?: number;
  asciiRatio?: number;
  columnsDetected?: number;
  tablesDetected?: number;
  note?: string;
  events?: { ts: number; text: string }[];
}

type TabType = 'upload' | 'rescrap' | 'youtube';

const formatDiscoveryStat = (value?: number): string | null => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return null;
  }
  return value.toLocaleString();
};

const DocumentUpload: React.FC<DocumentUploadProps> = ({ clientId }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>(() => {
    const savedTab = localStorage.getItem('dataImportActiveTab');
    return (savedTab === 'upload' || savedTab === 'rescrap' || savedTab === 'youtube') ? savedTab : 'upload';
  });
  const [files, setFiles] = useState<File[]>([]);
  const [fileProgresses, setFileProgresses] = useState<FileProgress[]>([]);
  const [overallProgress, setOverallProgress] = useState<number>(0);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [isRescrapLoading, setIsRescrapLoading] = useState<boolean>(false);
  const [url, setUrl] = useState<string>('');
  const [scrapingMethod, setScrapingMethod] = useState<'internal' | 'firecrawl'>('firecrawl');
  const [botPurpose, setBotPurpose] = useState<string>('general');
  const [customPrompt, setCustomPrompt] = useState<string>('');
  const [showPromptEditor, setShowPromptEditor] = useState<boolean>(false);
  const [showCrawlProgress, setShowCrawlProgress] = useState<boolean>(false);
  const [crawlStatus, setCrawlStatus] = useState<any>(null);
  const [isPreScanning, setIsPreScanning] = useState<boolean>(false);
  const [preScannedPages, setPreScannedPages] = useState<any[]>([]);
  const [totalDiscoveredPages, setTotalDiscoveredPages] = useState<number>(0);
  const [selectedPages, setSelectedPages] = useState<Set<string>>(new Set());
  const [hasManualSelection, setHasManualSelection] = useState<boolean>(false);
  const [showPageSelector, setShowPageSelector] = useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState<'url' | 'scanning' | 'categories' | 'bot-purpose' | 'page-selection'>('url');
  const [llmAnalysis, setLlmAnalysis] = useState<any>(null);
  const [botRecommendations, setBotRecommendations] = useState<any[]>([]);
  const [recommendedBotType, setRecommendedBotType] = useState<string>('');
  const [categoriesDetail, setCategoriesDetail] = useState<any[]>([]);
  const [discoverySummary, setDiscoverySummary] = useState<any | null>(null);
  const [activeCategoryTooltip, setActiveCategoryTooltip] = useState<string | null>(null);
  const aggregatedCategories = React.useMemo(
    () => aggregateCategoryDetails(categoriesDetail),
    [categoriesDetail]
  );
  const categoryPagesMap = React.useMemo(() => {
    if (!aggregatedCategories.length || !preScannedPages.length) {
      return new Map<string, any[]>();
    }

    const map = new Map<string, any[]>();
    const seenUrls = new Map<string, Set<string>>();

    aggregatedCategories.forEach(cat => {
      map.set(cat.name, []);
      seenUrls.set(cat.name, new Set());
    });

    preScannedPages.forEach(page => {
      if (!page || !page.url || !page.category) return;
      const leafName = getLeafCategoryName(page.category).toLowerCase();
      aggregatedCategories.forEach(cat => {
        const matchesLeaf = leafName === cat.name.toLowerCase();
        const matchesRaw = cat.rawNames.some(raw => raw && raw.toLowerCase() === page.category.toLowerCase());
        if (matchesLeaf || matchesRaw) {
          const seenForCategory = seenUrls.get(cat.name);
          if (seenForCategory && !seenForCategory.has(page.url)) {
            seenForCategory.add(page.url);
            map.get(cat.name)?.push(page);
          }
        }
      });
    });

    return map;
  }, [aggregatedCategories, preScannedPages]);

  // Debug logging for bot recommendations
  React.useEffect(() => {
    console.log('[DOCUMENT-UPLOAD] botRecommendations state updated:', botRecommendations);
    console.log('[DOCUMENT-UPLOAD] recommendedBotType state updated:', recommendedBotType);
    console.log('[DOCUMENT-UPLOAD] llmAnalysis state updated:', llmAnalysis);
  }, [botRecommendations, recommendedBotType, llmAnalysis]);

  React.useEffect(() => {
    setHasManualSelection(false);
  }, [botPurpose]);

  React.useEffect(() => {
    setActiveCategoryTooltip(null);
  }, [currentStep, aggregatedCategories.length]);

  // Auto-align selected pages with current bot type when the user hasn't manually changed selection
  React.useEffect(() => {
    if (currentStep !== 'page-selection') return;
    if (!preScannedPages.length || !botRecommendations.length) return;
    if (hasManualSelection) return;

    const selectedBotRec = botRecommendations.find(rec =>
      rec.bot_type === botPurpose ||
      rec.bot_type.toLowerCase().replace(/ /g, '-') === botPurpose ||
      botPurpose === rec.bot_type.toLowerCase().replace(/ /g, '-')
    );

    const botCategories = Array.isArray(selectedBotRec?.category_names) ? selectedBotRec.category_names : [];
    const shouldFilterPages = shouldFilterByBotCategories(botCategories, botPurpose, selectedBotRec?.bot_type);
    const filteredPagesBase = shouldFilterPages
      ? preScannedPages.filter(page => page.category && botCategories.includes(page.category))
      : preScannedPages;

    if (!filteredPagesBase.length) {
      return;
    }

    const totalLimit = selectedBotRec?.total_urls
      ? Math.min(selectedBotRec.total_urls, filteredPagesBase.length, MAX_PAGE_SELECTION)
      : Math.min(filteredPagesBase.length, MAX_PAGE_SELECTION);

    const limitedPages = filteredPagesBase.slice(0, totalLimit);
    if (!limitedPages.length) return;

    const limitedSet = new Set(limitedPages.map(page => page.url));
    const matches =
      limitedSet.size === selectedPages.size &&
      Array.from(limitedSet).every(url => selectedPages.has(url));

    if (!matches) {
      setSelectedPages(limitedSet);
    }
  }, [currentStep, botPurpose, botRecommendations, preScannedPages, hasManualSelection, selectedPages]);
  const [showRecrawlTooltip, setShowRecrawlTooltip] = useState<boolean>(false);
  // State for progressive page discovery animation
  const [discoveredPages, setDiscoveredPages] = React.useState<any[]>([]);
  const [scanProgress, setScanProgress] = React.useState(0);
  const [scanMessage, setScanMessage] = React.useState('');
  const [totalFromSitemap, setTotalFromSitemap] = React.useState(0);  // Total URLs found in sitemap
  const [totalAnalyzedByLLM, setTotalAnalyzedByLLM] = React.useState(0);  // URLs analyzed (max 1000)
  const [isValidUrl, setIsValidUrl] = useState<boolean>(false);
  // Preview functionality removed
  const abortControllerRef = useRef<AbortController | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadStreamRef = useRef<EventSource | null>(null);
  const crawlStreamRef = useRef<EventSource | null>(null);
  const botPurposeRef = useRef<HTMLDivElement>(null);

  // Clean up streams on unmount
  useEffect(() => {
    return () => {
      if (uploadStreamRef.current) {
        uploadStreamRef.current.close();
        uploadStreamRef.current = null;
      }
      if (crawlStreamRef.current) {
        crawlStreamRef.current.close();
        crawlStreamRef.current = null;
      }
    };
  }, []);

  // Save active tab to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('dataImportActiveTab', activeTab);
  }, [activeTab]);

  // Calculate overall progress whenever individual file progress changes
  useEffect(() => {
    if (fileProgresses.length === 0) {
      setOverallProgress(0);
      return;
    }

    const totalProgress = fileProgresses.reduce((acc, file) => acc + file.progress, 0);
    setOverallProgress(Math.floor(totalProgress / fileProgresses.length));
  }, [fileProgresses]);

  // Update prompt when bot purpose changes
  useEffect(() => {
    if (BOT_PURPOSE_PROMPTS[botPurpose as keyof typeof BOT_PURPOSE_PROMPTS]) {
      setCustomPrompt(BOT_PURPOSE_PROMPTS[botPurpose as keyof typeof BOT_PURPOSE_PROMPTS].prompt);
    }
  }, [botPurpose]);

  // Validate URL format
  useEffect(() => {
    const validateUrl = (urlString: string) => {
      if (!urlString.trim()) return false;

      let urlToCheck = urlString.trim();

      // Add https:// if no protocol is specified
      if (!urlToCheck.startsWith('http://') && !urlToCheck.startsWith('https://')) {
        urlToCheck = 'https://' + urlToCheck;
      }

      try {
        const urlObj = new URL(urlToCheck);

        // Check if it has a valid protocol (http or https)
        if (urlObj.protocol !== 'http:' && urlObj.protocol !== 'https:') {
          return false;
        }

        // Check if hostname has at least one dot (e.g., example.com)
        // This ensures we have a TLD like .com, .net, etc.
        const hostname = urlObj.hostname;
        if (!hostname.includes('.')) {
          return false;
        }

        // Check if hostname has at least 2 parts (domain + TLD)
        const parts = hostname.split('.');
        if (parts.length < 2) {
          return false;
        }

        // Check if each part is not empty
        if (parts.some(part => part.length === 0)) {
          return false;
        }

        // Check if TLD (last part) has at least 2 characters
        // This ensures .com, .net, .org, etc. but rejects .n, .x, etc.
        const tld = parts[parts.length - 1];
        if (tld.length < 2) {
          return false;
        }

        // Check if domain name (second to last part) has at least 2 characters
        const domain = parts[parts.length - 2];
        if (domain.length < 2) {
          return false;
        }

        return true;
      } catch {
        return false;
      }
    };

    const valid = validateUrl(url.trim());
    setIsValidUrl(valid);
  }, [url]);

  // Smooth scroll to bot purpose selection when valid URL is entered
  useEffect(() => {
    if (isValidUrl && botPurposeRef.current) {
      setTimeout(() => {
        botPurposeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 100);
    }
  }, [isValidUrl]);

  const getFileIcon = (fileType: string) => {
    const type = fileType.toLowerCase();
    if (type === 'pdf') return <FileText size={20} className="text-red-500" />;
    if (type === 'doc' || type === 'docx') return <FileText size={20} className="text-brand-500" />;
    if (type === 'xls' || type === 'xlsx') return <FileSpreadsheet size={20} className="text-brand-600" />;
    if (type === 'csv') return <FileSpreadsheet size={20} className="text-brand-500" />;
    if (type === 'txt') return <FileText size={20} className="text-gray-500" />;
    if (type === 'md' || type === 'json') return <FileCode size={20} className="text-brand-500" />;
    return <File size={20} className="text-gray-500" />;
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    
    if (isProcessing) return;
    
    const droppedFiles = Array.from(e.dataTransfer.files);
    handleFiles(droppedFiles);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isProcessing || !e.target.files?.length) return;
    
    const selectedFiles = Array.from(e.target.files);
    handleFiles(selectedFiles);
  };

  const handleFiles = (newFiles: File[]) => {
    const acceptedFiles: File[] = [];
    const rejectedFiles: File[] = [];
    
    // Check file types and create progress trackers
    const fileProgressData: FileProgress[] = [];

    newFiles.forEach(file => {
      const extension = file.name.split('.').pop()?.toLowerCase();
      
      if (extension && ['pdf', 'doc', 'docx', 'txt', 'xlsx', 'xls', 'csv', 'md', 'json'].includes(extension)) {
        acceptedFiles.push(file);
        
        // Create progress tracker
        fileProgressData.push({
          id: `file-${Date.now()}-${Math.random().toString(16).slice(2)}`,
          name: file.name,
          progress: 0,
          status: 'pending',
          fileType: extension,
          size: file.size,
          message: 'Uploading...',
        });
      } else {
        rejectedFiles.push(file);
      }
    });

    if (rejectedFiles.length > 0) {
      toast.error(`${rejectedFiles.length} file(s) were rejected. Only PDF, DOC, DOCX, TXT, XLSX, XLS, CSV, MD, and JSON files are supported.`);
    }

    if (acceptedFiles.length > 0) {
      setFiles(prev => [...prev, ...acceptedFiles]);
      setFileProgresses(prev => [...prev, ...fileProgressData]);
      
      // Automatically start upload
      setTimeout(() => {
        handleUpload([...acceptedFiles]);
      }, 100);
    }
  };

  const handleUpload = async (filesToUpload = files) => {
    if (!clientId || filesToUpload.length === 0 || isProcessing) return;

    setIsProcessing(true);
    abortControllerRef.current = new AbortController();

    try {
      // Create a formData object to send files
      const formData = new FormData();
      
      // Append all files
      filesToUpload.forEach(file => {
        formData.append('documents', file);
      });
      
      // Add client ID
      formData.append('client_id', clientId);

      // Update all files to "uploading" status
      setFileProgresses(prev => 
        prev.map(fp => ({...fp, status: 'uploading', progress: 5}))
      );

      // Use the new streaming upload function
      const response = await uploadDocumentsWithProgress(
        formData,
        (progressData) => {
          console.log('Progress update:', progressData);
          
          if (progressData.status && progressData.files) {
            // Update progress for each file
            setFileProgresses(prev => 
              prev.map(fp => {
                const serverFile = progressData.files.find((f: any) => f.name === fp.name);
                
                if (!serverFile) return fp;
                
                // Check if the file is in any stage of processing
                const isCompleted = serverFile.status === 'completed';
                const isProcessing = ['processing', 'uploading', 'saved to database', 'queued for knowledge base creation'].includes(serverFile.status);
                const hasError = serverFile.status === 'failed' || serverFile.error;
                
                // Calculate combined progress that takes into account both file upload and embedding
                let combinedProgress = (typeof serverFile.progress_percentage === 'number'
                  ? serverFile.progress_percentage
                  : (typeof serverFile.progress === 'number' ? serverFile.progress : fp.progress));
                
                // Include detailed processing stage info if available
                const processingStage = serverFile.processing_stage || 'processing';
                
                // Include embedding progress if available
                const embeddingProgress = serverFile.embedding_progress || 0;

                // Per-page telemetry
                const pageCurrent = serverFile.page_current ?? fp.pageCurrent;
                const pageTotal = serverFile.page_total ?? fp.pageTotal;
                const pageMode = serverFile.page_mode ?? fp.pageMode;
                const pageMethod = serverFile.page_method ?? fp.pageMethod;
                const pageConfidence = serverFile.page_confidence ?? fp.pageConfidence;
                const asciiRatio = serverFile.ascii_ratio ?? fp.asciiRatio;
                const columnsDetected = serverFile.columns_detected ?? fp.columnsDetected;
                const tablesDetected = serverFile.tables_detected ?? fp.tablesDetected;
                const note = serverFile.note ?? fp.note;

                // Build compact event line
                let events = fp.events ? [...fp.events] : [];
                if (serverFile.page_current && serverFile.page_total) {
                  const eventText = `Page ${serverFile.page_current}/${serverFile.page_total}` +
                    (serverFile.page_mode ? ` • ${serverFile.page_mode}` : '') +
                    (serverFile.page_method ? ` (${serverFile.page_method})` : '') +
                    (serverFile.page_confidence ? ` • conf ${Math.round(serverFile.page_confidence)}` : '') +
                    (serverFile.columns_detected ? ` • cols ${serverFile.columns_detected}` : '') +
                    (serverFile.tables_detected ? ` • tables ${serverFile.tables_detected}` : '') +
                    (serverFile.note ? ` • ${serverFile.note}` : '');
                  // Avoid duplicating identical consecutive events
                  if (!events.length || events[events.length - 1].text !== eventText) {
                    events.push({ ts: Date.now(), text: eventText });
                    if (events.length > 5) events = events.slice(events.length - 5);
                  }
                }
                
                return {
                  ...fp,
                  progress: isCompleted ? 100 : combinedProgress,
                  status: isCompleted ? 'completed' : (hasError ? 'failed' : (isProcessing ? 'processing' : serverFile.status)),
                  message: isCompleted ? 'Successfully processed' : (serverFile.error || fp.message),
                  processingStage,
                  embeddingProgress,
                  pageCurrent,
                  pageTotal,
                  pageMode,
                  pageMethod,
                  pageConfidence,
                  asciiRatio,
                  columnsDetected,
                  tablesDetected,
                  note,
                  events
                };
              })
            );
            
            // Only show success toast when all files are fully processed
            if (progressData.status === 'completed') {
              const successCount = progressData.successful_files || progressData.processed_files || fileProgresses.filter(fp => fp.status === 'completed').length;
              toast.success(`Successfully uploaded ${successCount} document(s)`);
              
              // Reset after 3 seconds
              setTimeout(() => {
                if (fileInputRef.current) fileInputRef.current.value = '';
                setFiles([]);
                setFileProgresses([]);
                setIsProcessing(false);
              }, 3000);
            }
          }
          
          // Handle upload failure
          if (progressData.status === 'failed') {
            toast.error('Document upload failed. Please try again.');
            setIsProcessing(false);
          }
        },
        (error) => {
          console.error('Upload stream error:', error);
          toast.error('Error monitoring upload progress');
        }
      );

      // Store the streaming connection for cleanup
      if (response && response.closeStream) {
        const closeStreamFn = response.closeStream;
        uploadStreamRef.current = {
          close: closeStreamFn
        } as any;
      }
    } catch (error) {
      console.error('Upload error:', error);
      
      // Mark all files as failed
      setFileProgresses(prev => 
        prev.map(fp => ({
          ...fp, 
          status: 'failed', 
          message: 'Upload failed. Please try again.'
        }))
      );
      
      toast.error('Document upload failed. Please try again.');
      setIsProcessing(false);
    }
  };

  // Extract title from URL for display
  const extractTitleFromUrl = (url: string): string => {
    try {
      const urlObj = new URL(url);
      const path = urlObj.pathname.replace(/\/$/, '');
      const lastSegment = path.split('/').pop() || 'Home';
      return lastSegment
        .replace(/-/g, ' ')
        .replace(/_/g, ' ')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    } catch {
      return 'Page';
    }
  };

  const handlePreScan = async () => {
    if (!url.trim()) return;

    setIsPreScanning(true);
    setCurrentStep('scanning');  // Show scanning UI
    setDiscoveredPages([]);
    setScanProgress(0);
    setScanMessage('Connecting to server...');
    setTotalFromSitemap(0);
    setTotalAnalyzedByLLM(0);
    setTotalDiscoveredPages(0);
    setDiscoverySummary(null);

    try {
      // Use SSE for real-time streaming preview
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const token = localStorage.getItem('auth_token');

      const params = new URLSearchParams();
      params.set('url', url);
      params.set('is_demo', 'false');
      if (clientId) params.set('client_id', clientId);
      if (recommendedBotType) params.set('bot_type', recommendedBotType);
      if (token) params.set('token', token);

      const streamUrl = `${apiUrl}/api/crawl/preview-stream?${params.toString()}`;

      const eventSource = new EventSource(streamUrl);

      // Handle status updates
      eventSource.addEventListener('status', (e: any) => {
        const data = JSON.parse(e.data);
        setScanMessage(data.message);
        setScanProgress(data.progress);
      });

      // Handle page discovery batches
      eventSource.addEventListener('page_batch', (e: any) => {
        const data = JSON.parse(e.data);

        // Add discovered pages to the list
        setDiscoveredPages(prev => [...prev, ...data.pages]);
        setScanProgress(data.progress);
        setScanMessage(`Discovered ${data.total_discovered} pages...`);
      });

      // Handle discovery complete
      eventSource.addEventListener('discovery_complete', (e: any) => {
        const data = JSON.parse(e.data);
        setTotalFromSitemap(data.total_discovered);
        setTotalAnalyzedByLLM(data.analyzing);
        setScanMessage(data.message);
        setScanProgress(data.progress);
      });

      eventSource.addEventListener('categorization_progress', (e: any) => {
        const data = JSON.parse(e.data);
        setScanMessage(data.message || '🤖 Categorizing pages with AI...');
        if (typeof data.progress === 'number') {
          setScanProgress(prev => Math.max(prev, data.progress));
        }
        if (typeof data.categorized_urls === 'number') {
          setTotalAnalyzedByLLM(data.categorized_urls);
        }
      });

      // Handle categorization complete
      eventSource.addEventListener('categorization_complete', (e: any) => {
        const data = JSON.parse(e.data);
        setScanMessage(`✅ Site type: ${data.site_type}`);
        setScanProgress(data.progress);
      });

      // Handle bot recommendations
      eventSource.addEventListener('bot_recommendations', (e: any) => {
        try {
          const rawData = e.data;
          console.log('[DOCUMENT-UPLOAD] Raw SSE data received:', rawData);
          const data = JSON.parse(rawData);
          console.log('[DOCUMENT-UPLOAD] Parsed bot recommendations:', data);
          console.log('[DOCUMENT-UPLOAD] all_bot_types:', data.all_bot_types);

          if (data.all_bot_types) {
            data.all_bot_types.forEach((bot: any, index: number) => {
              console.log(`[DOCUMENT-UPLOAD] Bot ${index}:`, {
                bot_type: bot.bot_type,
                total_urls: bot.total_urls,
                score: bot.score
              });
            });
          }

          setBotRecommendations(data.all_bot_types || []);
          setRecommendedBotType(data.recommended || 'General Assistant');
          setScanMessage(`🤖 Recommended: ${data.recommended}`);
          setScanProgress(data.progress);
        } catch (error) {
          console.error('[DOCUMENT-UPLOAD] Error parsing bot recommendations:', error);
          console.error('[DOCUMENT-UPLOAD] Raw data:', e.data);
        }
      });

      // Handle complete event
      eventSource.addEventListener('complete', (e: any) => {
        const data = JSON.parse(e.data);

        setDiscoverySummary(data.discovery_summary || null);
        // Store all final data
        setPreScannedPages(data.preview_pages || []);
        setCategoriesDetail(data.categories_detail || []);
        setTotalFromSitemap(data.total_discovered);
        setTotalAnalyzedByLLM(data.total_analyzed);
        setTotalDiscoveredPages(data.total_filtered);

        // Select all pages by default
        const allUrls = new Set<string>((data.preview_pages || []).map((page: any) => page.url as string));
        setSelectedPages(allUrls);
        setHasManualSelection(false);

        setScanMessage(data.message);
        setScanProgress(100);

        // Close connection
        eventSource.close();
        setIsPreScanning(false);

        // Move to categories step after a brief delay
        setTimeout(() => {
          setCurrentStep('categories');
        }, 500);
      });

      // Handle errors
      eventSource.addEventListener('error', (e: any) => {
        try {
          const data = JSON.parse(e.data);
          toast.error(data.message || 'An error occurred during scanning');
        } catch {
          toast.error('Connection error. Please try again.');
        }
        eventSource.close();
        setIsPreScanning(false);
        setCurrentStep('url');
      });

      // Handle connection errors
      eventSource.onerror = (error) => {
        console.error('EventSource error:', error);
        eventSource.close();

        if (scanProgress === 0) {
          toast.error('Failed to connect to server. Please try again.');
        } else {
          toast.error('Connection lost. Please try again.');
        }

        setIsPreScanning(false);
        setCurrentStep('url');
      };

    } catch (error) {
      console.error('Pre-scan error:', error);
      toast.error('Failed to start website scan');
      setIsPreScanning(false);
      setCurrentStep('url');
    }
  };

  const handleRescrap = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!clientId || !url.trim()) {
      toast.error('Please enter a valid URL');
      return;
    }

    setIsRescrapLoading(true);

    try {
      const truncatedSelectedPages = selectedPages.size > 0 ? Array.from(selectedPages).slice(0, MAX_PAGE_SELECTION) : null;
      const selectedCount = truncatedSelectedPages ? truncatedSelectedPages.length : 1;
      const cappedSelectedCount = Math.min(selectedCount, MAX_PAGE_SELECTION);

      // Create a crawl job to track the progress
      const jobId = `crawl-${Date.now()}`;
      addCrawlJob({
        id: jobId,
        clientId: clientId,
        url: url,
        status: 'initializing',
        pages_crawled: 0,  // Note: This will be updated from SSE with pages_scraped/pages_processed
        pages_in_queue: cappedSelectedCount,
        max_pages: cappedSelectedCount,
        progress_percentage: 0
      });

      // Navigate to details page FIRST (restore original behavior)
      navigate(`/crawl-details/${clientId}`, { 
        state: { 
          fromRescrap: true,
          pendingUrl: url,
          botPurpose: botPurpose,
          customPrompt: showPromptEditor ? customPrompt : null,
          selectedPages: truncatedSelectedPages ?? []
        }
      });
      
      // THEN start the crawling process with the selected pages and bot purpose
      const result = await rescrapWebsite(
        clientId,
        url,
        undefined,
        false,
        'firecrawl',
        false,
        botPurpose,
        showPromptEditor ? customPrompt : undefined,
        truncatedSelectedPages ?? undefined,  // Pass the selected pages (capped at MAX_PAGE_SELECTION)
        botPurpose  // Pass bot_type (same as botPurpose for now)
      );
      toast.success(`Website crawling started`);
      
      setUrl('');
      setSelectedPages(new Set());
      setHasManualSelection(false);
      setPreScannedPages([]);
      setCurrentStep('url');
    } catch (error) {
      console.error('Rescrap error:', error);
      toast.error('Failed to crawl website');
      // If there was an error, we should navigate back
      navigate('/');
    } finally {
      setIsRescrapLoading(false);
    }
  };

  const cancelUpload = () => {
    // Close the streaming connection
    if (uploadStreamRef.current) {
      uploadStreamRef.current.close();
      uploadStreamRef.current = null;
    }
    
    // Cancel the HTTP request if still in progress
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    
    setFiles([]);
    setFileProgresses([]);
    setIsProcessing(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  const getProcessingStageInfo = (stage: string) => {
    const stageInfo: { [key: string]: { icon: string; text: string; description?: string } } = {
      'uploading': {
        icon: '📤',
        text: 'Uploading file to server',
        description: 'Transferring your document securely'
      },
      'processing': {
        icon: '🔍',
        text: 'Analyzing document content',
        description: 'Extracting text and metadata'
      },
      'saved to database': {
        icon: '💾',
        text: 'Saving to database',
        description: 'Storing document information'
      },
      'queued for knowledge base creation': {
        icon: '🧠',
        text: 'Preparing for AI processing',
        description: 'Document queued for embedding'
      },
      'embedding': {
        icon: '🤖',
        text: 'Creating AI embeddings',
        description: 'Building searchable knowledge base'
      },
      'extracting': {
        icon: '📄',
        text: 'Extracting document text',
        description: 'Reading document content'
      },
      'ocr_processing': {
        icon: '👁️',
        text: 'OCR Processing',
        description: 'Converting scanned images to text'
      }
    };

    return stageInfo[stage] || { icon: '⚙️', text: stage };
  };

  const TabButton: React.FC<{ tab: TabType; label: string }> = ({ tab, label }) => (
    <button
      onClick={() => setActiveTab(tab)}
      className={`flex-1 py-3 px-6 text-sm font-medium rounded-lg transition-colors duration-200
          ${activeTab === tab 
              ? 'bg-brand-600 text-white' 
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
    >
      {label}
    </button>
  );

  return (
    <div className="">
      <h1 className="text-2xl font-bold mb-8">
        {activeTab === 'upload' ? 'Upload Documents' : activeTab === 'rescrap' ? 'Website Scraper' : 'YouTube Videos'}
      </h1>

      {/* Tabs */}
      <div className="flex space-x-4 mb-8">
        <TabButton tab="upload" label="Upload Additional Documents" />
        <TabButton tab="rescrap" label="Re-scrape Website" />
        <TabButton tab="youtube" label="YouTube Videos" />
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-[30px] p-8 shadow-sm">
        {activeTab === 'upload' ? (
          <div className="text-center">
            <Upload size={32} className="text-brand-500 mb-4 mx-auto" />
            <h2 className="text-xl font-semibold mb-4">Upload Additional Documents</h2>
            <p className="text-gray-600 mb-6">
              Upload documents, spreadsheets, and other supported files to enhance your knowledge base.
              These files will be added to your existing knowledge base.
            </p>

            {/* Upload Area */}
            <div className="max-w-md mx-auto">
              <div 
                className={`
                  border-2 border-dashed rounded-lg p-6 text-center 
                  ${isDragging ? 'border-brand-500 bg-brand-50' : 'border-brand-300'} 
                  ${isProcessing ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-brand-50'}
                  transition-colors duration-200
                `}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => !isProcessing && fileInputRef.current?.click()}
              >
                <input
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.txt,.xlsx,.xls,.csv,.md,.json"
                  className="hidden"
                  onChange={handleFileInput}
                  ref={fileInputRef}
                  disabled={isProcessing}
                />
                
                {isProcessing ? (
                  <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500 mb-2"></div>
                    <p className="text-gray-500">Processing documents...</p>
                  </div>
                ) : (
                  <div className="text-center">
                    <p className="text-brand-500 font-medium">Click to select files or drag and drop</p>
                    <p className="text-sm text-gray-500 mt-1">
                      Supported: PDF, DOC, DOCX, TXT, XLSX, XLS, CSV, MD, JSON
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* File List with Progress */}
            {fileProgresses.length > 0 && (
              <div className="mt-8">
                <h3 className="text-lg font-semibold mb-4">Files ({fileProgresses.length})</h3>
                <div className="space-y-4">
                  {fileProgresses.map((file) => (
                    <div key={file.id} className="bg-gradient-to-r from-gray-50 to-white border border-gray-200 rounded-card p-6 shadow-sm hover:shadow-md transition-shadow">
                      {/* File Header */}
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <div className="relative">
                            {file.status === 'uploading' || file.status === 'processing' ? (
                              <div className="relative">
                                <Loader2 size={24} className="text-brand-500 animate-spin" />
                                <div className="absolute -inset-1 bg-brand-100 rounded-full animate-ping opacity-75"></div>
                              </div>
                            ) : file.status === 'completed' ? (
                              <div className="bg-brand-100 p-2 rounded-full">
                                <Check size={20} className="text-brand-600" />
                              </div>
                            ) : file.status === 'failed' ? (
                              <div className="bg-red-100 p-2 rounded-full">
                                <AlertTriangle size={20} className="text-red-600" />
                              </div>
                            ) : (
                              <div className="bg-gray-100 p-2 rounded-full">
                                {getFileIcon(file.fileType)}
                              </div>
                            )}
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900 text-base">{file.name}</h4>
                            <p className="text-sm text-gray-500 mt-1">
                              {formatFileSize(file.size)} • {file.fileType.toUpperCase()}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className="text-2xl font-bold text-gray-700">{file.progress}%</span>
                        </div>
                      </div>

                      {/* Processing Stage Indicator */}
                      {file.processingStage && file.status !== 'completed' && file.status !== 'failed' && (
                        <div className="mb-3 bg-brand-50 border border-brand-200 rounded-lg p-4">
                          <div className="flex items-start space-x-3">
                            <div className="flex space-x-1 mt-1">
                              <div className="w-2 h-2 bg-brand-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                              <div className="w-2 h-2 bg-brand-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                              <div className="w-2 h-2 bg-brand-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                            </div>
                            <div className="flex-1">
                              <p className="text-base font-semibold text-brand-900">
                                {getProcessingStageInfo(file.processingStage).icon} {getProcessingStageInfo(file.processingStage).text}
                              </p>
                              {getProcessingStageInfo(file.processingStage).description && (
                                <p className="text-sm text-brand-700 mt-1">
                                  {getProcessingStageInfo(file.processingStage).description}
                                </p>
                              )}
                            </div>
                            {(file.pageCurrent && file.pageTotal) && (
                              <span className="ml-auto inline-flex items-center px-2 py-0.5 text-[10px] font-semibold rounded bg-brand-100 text-brand-700 border border-brand-200">
                                LIVE
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Main Progress Bar */}
                      <div className="mb-3">
                        <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                          <div 
                            className={`h-3 rounded-full transition-all duration-500 ease-out relative overflow-hidden ${
                              file.status === 'completed' ? 'bg-brand-500' :
                              file.status === 'failed' ? 'bg-red-500' : 'bg-brand-500'
                            }`}
                            style={{ width: `${file.progress}%` }}
                          >
                            {file.status !== 'completed' && file.status !== 'failed' && (
                              <div className="absolute inset-0 bg-white/30 animate-pulse"></div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Page-level status */}
                      {(file.pageCurrent && file.pageTotal) && (
                        <div className="text-xs text-gray-700 mb-2">
                          <span className="font-medium">Page {file.pageCurrent}/{file.pageTotal}</span>
                          {file.pageMode && (
                            <span className="ml-2 px-2 py-0.5 rounded bg-gray-100">{file.pageMode}{file.pageMethod ? ` (${file.pageMethod})` : ''}</span>
                          )}
                          {typeof file.pageConfidence === 'number' && (
                            <span className="ml-2">conf {Math.round(file.pageConfidence)}</span>
                          )}
                          {typeof file.asciiRatio === 'number' && (
                            <span className="ml-2">ascii {(file.asciiRatio * 100).toFixed(0)}%</span>
                          )}
                          {file.columnsDetected && (
                            <span className="ml-2">cols {file.columnsDetected}</span>
                          )}
                          {typeof file.tablesDetected === 'number' && (
                            <span className="ml-2">tables {file.tablesDetected}</span>
                          )}
                        </div>
                      )}

                      {/* Compact event log */}
                      {file.events && file.events.length > 0 && (
                        <div className="text-[11px] text-gray-600 space-y-1 mb-2">
                          {file.events.map(e => (
                            <div key={e.ts} className="truncate">{e.text}</div>
                          ))}
                        </div>
                      )}

                      {/* Embedding Progress (Knowledge Base Creation) */}
                      {file.embeddingProgress !== undefined && file.embeddingProgress > 0 && file.progress >= 80 && file.progress < 100 && (
                        <div className="mb-3 ml-8">
                          <div className="flex justify-between items-center text-sm mb-1">
                            <span className="text-brand-700 font-medium">🧠 Creating knowledge base entries</span>
                            <span className="text-brand-700 font-semibold">{file.embeddingProgress}%</span>
                          </div>
                          <div className="w-full bg-brand-100 rounded-full h-2 overflow-hidden">
                            <div 
                              className="h-2 rounded-full transition-all duration-300 bg-brand-500 relative overflow-hidden"
                              style={{ width: `${file.embeddingProgress}%` }}
                            >
                              <div className="absolute inset-0 bg-white/30 animate-pulse"></div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Status Message */}
                      {file.message && (
                        <div className={`mt-3 p-3 rounded-lg ${
                          file.status === 'completed' ? 'bg-brand-50 border border-brand-200' :
                          file.status === 'failed' ? 'bg-red-50 border border-red-200' : 
                          'bg-brand-50 border border-brand-200'
                        }`}>
                          <p className={`text-sm font-medium ${
                            file.status === 'completed' ? 'text-brand-700' :
                            file.status === 'failed' ? 'text-red-700' : 
                            'text-brand-700'
                          }`}>
                            {file.message}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Cancel Button */}
                {isProcessing && (
                  <div className="mt-6 flex justify-end">
                    <button
                      type="button"
                      onClick={cancelUpload}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : activeTab === 'rescrap' ? (
          <div className="max-w-4xl mx-auto">

            {currentStep === 'url' && (
              <>
                {/* Step 1: URL Input with Pre-scan */}
                <div className="mb-8">
                  <div className="flex items-center mb-3">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                      <span className="bg-brand-500 text-white rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm">1</span>
                      Website URL
                    </h3>
                    <div className="relative ml-2">
                      <button
                        onClick={() => setShowRecrawlTooltip(!showRecrawlTooltip)}
                        className="text-gray-400 hover:text-brand-500 transition-colors"
                        type="button"
                      >
                        <Info size={18} />
                      </button>

                      {showRecrawlTooltip && (
                        <>
                          {/* Backdrop to close tooltip when clicking outside */}
                          <div
                            className="fixed inset-0 z-10"
                            onClick={() => setShowRecrawlTooltip(false)}
                          />

                          {/* Tooltip */}
                          <div className="absolute left-0 top-8 w-80 bg-white rounded-lg shadow-card border border-gray-200 p-4 z-20">
                            <div className="flex items-start space-x-3">
                              <AlertTriangle size={16} className="text-amber-500 mt-0.5 flex-shrink-0" />
                              <div className="text-sm text-gray-700">
                                <p className="font-semibold text-gray-900 mb-2">Re-scraping will replace your website content</p>
                                <p className="mb-1">• Only website pages will be replaced with fresh content</p>
                                <p className="mb-1">• Your uploaded documents (PDFs, Word, Excel) will remain unchanged</p>
                                <p>• YouTube videos in your knowledge base will remain unchanged</p>
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="relative">
                    <input
                      type="url"
                      value={url}
                      onChange={(e) => {
                        setUrl(e.target.value);
                        setPreScannedPages([]);
                      }}
                      placeholder="https://example.com"
                      required
                      disabled={isPreScanning}
                      className="w-full px-4 py-3 pr-24 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent text-lg"
                    />
                    {url && (
                      <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                        {isPreScanning ? (
                          <div className="flex items-center text-brand-600">
                            <Loader2 size={16} className="animate-spin mr-2" />
                            <span className="text-sm">Scanning...</span>
                          </div>
                        ) : preScannedPages.length > 0 ? (
                          <div className="text-brand-600">
                            <Check size={16} />
                          </div>
                        ) : null}
                      </div>
                    )}
                  </div>
                </div>

                {/* Next Button - Show only when valid URL is entered */}
                {isValidUrl && (
                  <div className="text-center">
                    <button
                      onClick={handlePreScan}
                      disabled={isPreScanning}
                      className={`
                        px-8 py-3 rounded-lg text-white font-semibold text-lg relative
                        ${isPreScanning
                          ? 'bg-gray-400 cursor-not-allowed'
                          : 'bg-brand-600 hover:bg-brand-700 transform hover:scale-105'}
                        transition-all duration-200 shadow-lg hover:shadow-card
                      `}
                    >
                      {isPreScanning ? (
                        <div className="flex items-center justify-center">
                          <Loader2 size={16} className="animate-spin mr-2" />
                          <span>Analyzing with AI...</span>
                        </div>
                      ) : (
                        <>
                          <Bug size={16} className="mr-2 inline" />
                          Scan & Analyze Website
                          <ArrowRight size={16} className="ml-2 inline" />
                        </>
                      )}
                    </button>
                  </div>
                )}
              </>
            )}

            {currentStep === 'scanning' && (
              <>
                {/* Scanning Progress - Matches Demo UI */}
                <div className="bg-white rounded-2xl shadow-card p-8 border border-surface-border">
                  <div className="rounded-card border border-gray-200 p-6 bg-white">
                    <div className="space-y-4">
                      {/* Progress bar */}
                      <div className="rounded-full h-2 overflow-hidden bg-gray-100">
                        <div
                          className="bg-gradient-to-r from-brand-500 to-brand-500 h-full transition-all duration-300"
                          style={{ width: `${scanProgress}%` }}
                        />
                      </div>

                      {/* Status message */}
                      <p className="text-center text-sm text-gray-600">
                        {scanMessage || 'Discovering pages...'}
                      </p>

                      {/* Beautiful animated loading dots */}
                      {isPreScanning && (
                        <div className="flex justify-center items-center space-x-2 mt-4">
                          <div className="w-3 h-3 bg-brand-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                          <div className="w-3 h-3 bg-brand-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                          <div className="w-3 h-3 bg-brand-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                        </div>
                      )}

                      {/* Live page discovery feed - Show ALL discovered pages */}
                      {discoveredPages.length > 0 && (
                        <div className="mt-4 border-t pt-4">
                          <p className="text-xs text-gray-500 mb-2">
                            Discovered pages ({discoveredPages.length}):
                          </p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-64 overflow-y-auto border rounded-lg p-3 bg-gray-50">
                            {discoveredPages.map((page: any, idx: number) => (
                              <div key={idx} className="flex items-center space-x-2 text-sm animate-fade-in p-2 rounded bg-white shadow-sm border">
                                <CheckCircle size={16} className="text-brand-500 flex-shrink-0" />
                                <a
                                  href={page.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="truncate flex-1 text-brand-600 hover:text-brand-800 hover:underline transition-colors duration-200"
                                  title={`Visit: ${page.url}`}
                                >
                                  {page.title || page.url || 'Page'}
                                </a>
                                {page.category && (
                                  <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                                    {page.category}
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}

            {currentStep === 'categories' && (
              <>
                {/* Step 2: Show Categories & Bot Type Selection */}
                <div className="bg-white rounded-2xl shadow-card p-8 border border-surface-border">

                  {/* Scan Complete Message - Beautiful UI from Demo */}
                  <div className="mb-8">
                    <div className="bg-gradient-to-br from-brand-50 to-brand-50 border border-brand-200 rounded-card p-6">
                      <div className="text-center mb-4">
                        <Check size={40} className="text-brand-500 mb-3 mx-auto" />
                        <h3 className="text-2xl font-bold text-gray-800">
                          Found {totalFromSitemap > 0 ? totalFromSitemap : totalDiscoveredPages} pages
                          {totalFromSitemap > totalAnalyzedByLLM && (
                            <span className="text-lg text-gray-600"> (analyzed {totalAnalyzedByLLM})</span>
                          )} across {aggregatedCategories.length} {aggregatedCategories.length === 1 ? 'category' : 'categories'}!
                        </h3>
                        {llmAnalysis && (
                          <p className="text-sm text-gray-600 mt-2">
                            {llmAnalysis.site_type && `${llmAnalysis.site_type} • `}
                            {llmAnalysis.analysis || 'Website content analyzed'}
                          </p>
                        )}
                        {discoverySummary && (
                          <div className="flex flex-wrap justify-center gap-2 mt-3 text-xs sm:text-sm text-gray-600">
                            {formatDiscoveryStat(discoverySummary?.unique_discovered) && (
                              <span className="px-3 py-1 bg-white border border-gray-200 rounded-full shadow-sm">
                                {formatDiscoveryStat(discoverySummary?.unique_discovered)} unique pages mapped
                              </span>
                            )}
                            {typeof discoverySummary?.duplicates_skipped === 'number' && discoverySummary.duplicates_skipped > 0 && (
                              <span className="px-3 py-1 bg-white border border-gray-200 rounded-full shadow-sm">
                                Removed {formatDiscoveryStat(discoverySummary?.duplicates_skipped)} duplicates
                              </span>
                            )}
                            {formatDiscoveryStat(discoverySummary?.preview_pages_total) && (
                              <span className="px-3 py-1 bg-white border border-gray-200 rounded-full shadow-sm">
                                {formatDiscoveryStat(discoverySummary?.preview_pages_total)} ready for review
                              </span>
                            )}
                            {formatDiscoveryStat(discoverySummary?.filtered_for_bot) && discoverySummary?.filtered_for_bot !== discoverySummary?.preview_pages_total && (
                              <span className="px-3 py-1 bg-white border border-gray-200 rounded-full shadow-sm">
                                {formatDiscoveryStat(discoverySummary?.filtered_for_bot)} match selected bot
                              </span>
                            )}
                            {formatDiscoveryStat(discoverySummary?.max_preview_limit) && (
                              <span className="px-3 py-1 bg-white border border-gray-200 rounded-full shadow-sm">
                                Limit: {formatDiscoveryStat(discoverySummary?.max_preview_limit)} URLs
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Categories - Inline Cards */}
                      <div className="mt-6">
                        <div className="max-h-72 overflow-y-auto pr-1">
                          <div className="flex flex-wrap gap-3">
                            {aggregatedCategories.map((cat: AggregatedCategory) => {
                              const pagesForCategory = categoryPagesMap.get(cat.name) || [];
                              return (
                                <InlineCategoryCard
                                  key={cat.name}
                                  categoryName={cat.name}
                                  icon={cat.icon}
                                  count={cat.count}
                                  listingCount={cat.listingCount}
                                  listingChildrenCount={cat.listingChildrenCount}
                                  sampleProducts={cat.sampleProductUrls}
                                  pages={pagesForCategory}
                                />
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Bot Type Selection with LLM Recommendations */}
                  <div className="mb-8">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      {botRecommendations.length > 0 ? 'AI-Recommended Bot Types' : 'Select Bot Purpose'}
                    </h3>

                    <BotPurposeSelector
                      selectedPurpose={botPurpose}
                      onPurposeChange={setBotPurpose}
                      disabled={false}
                      llmRecommendations={botRecommendations}
                      recommendedType={recommendedBotType}
                    />
                  </div>

                  {/* Navigation Buttons */}
                  <div className="flex justify-between items-center pt-6 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={() => setCurrentStep('url')}
                      className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition-colors"
                    >
                      <ArrowLeft size={16} className="mr-2 inline" />
                      Back
                    </button>
                    <button
                      type="button"
                      onClick={() => setCurrentStep('page-selection')}
                      className="px-8 py-3 bg-brand-600 hover:bg-brand-700 rounded-lg text-white font-semibold transition-all duration-200 shadow-lg hover:shadow-card transform hover:scale-105"
                    >
                      Next: Select Pages
                      <ArrowRight size={16} className="ml-2 inline" />
                    </button>
                  </div>
                </div>
              </>
            )}

            {currentStep === 'page-selection' && (
              <>
                {/* Enhanced Page Selection - Same as Consultant Flow */}
                <div className="bg-white rounded-2xl shadow-card p-8 border border-surface-border -m-8">
                  <div className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Select Pages to Crawl</h2>
                    <p className="text-gray-600">Choose which pages to include in your AI's knowledge base</p>

                    {/* Debug info for bot recommendations */}
                    {/* {process.env.NODE_ENV === 'development' && (
                      <div className="mb-4 p-3 bg-yellow-50 rounded-lg text-xs">
                        <p>Debug: botRecommendations length: {botRecommendations.length}</p>
                        <p>Debug: botPurpose: {botPurpose}</p>
                        <p>Debug: preScannedPages length: {preScannedPages.length}</p>
                        <p>Debug: selectedPages size: {selectedPages.size}</p>
                        <p>Debug: Sample bot rec: {JSON.stringify(botRecommendations.slice(0, 1))}</p>
                      </div>
                    )} */}
                  </div>

                  {/* Filter pages based on selected bot type */}
                  {(() => {
                    // Find the selected bot recommendation - handle both ID format and bot_type format
                    const selectedBotRec = botRecommendations.find(rec =>
                      rec.bot_type === botPurpose ||
                      rec.bot_type.toLowerCase().replace(/ /g, '-') === botPurpose ||
                      botPurpose === rec.bot_type.toLowerCase().replace(/ /g, '-')
                    );

                    console.log('[DEBUG] Bot type matching:', {
                      botPurpose,
                      botRecommendations: botRecommendations.map(r => ({ bot_type: r.bot_type, category_names: r.category_names })),
                      selectedBotRec: selectedBotRec ? { bot_type: selectedBotRec.bot_type, category_names: selectedBotRec.category_names } : null,
                      preScannedPagesLength: preScannedPages.length,
                      samplePageCategories: preScannedPages.slice(0, 3).map(p => ({ url: p.url, category: p.category }))
                    });

                    const botCategories = Array.isArray(selectedBotRec?.category_names) ? selectedBotRec.category_names : [];
                    const pagesWithCategories = preScannedPages.filter(page => page.category);
                    const shouldFilterPages = shouldFilterByBotCategories(
                      botCategories,
                      botPurpose,
                      selectedBotRec?.bot_type
                    ) && pagesWithCategories.length > 0;
                    const filteredPagesBase = shouldFilterPages
                      ? preScannedPages.filter(page => {
                          // Debug: log category matching
                          console.log(`Checking page ${page.url} with category "${page.category}" against bot categories:`, botCategories);
                          return page.category && botCategories.includes(page.category);
                        })
                      : preScannedPages;
                    const baseLength = filteredPagesBase.length;
                    const limitFromBot = selectedBotRec?.total_urls ?? baseLength;
                    const effectiveLimit = Math.min(limitFromBot, baseLength, MAX_PAGE_SELECTION);
                    const limitedPages = effectiveLimit > 0 ? filteredPagesBase.slice(0, effectiveLimit) : filteredPagesBase;

                    console.log('[DEBUG] Filtering results:', {
                      botCategories,
                      shouldFilterPages,
                      filteredPagesLength: baseLength,
                      pagesWithoutCategories: preScannedPages.filter(p => !p.category).length,
                      effectiveLimit,
                      sampleFilteredPages: limitedPages.slice(0, 3).map(p => ({ url: p.url, category: p.category })),
                      sampleAllPages: preScannedPages.slice(0, 3).map(p => ({ url: p.url, category: p.category }))
                    });

                    // Note: Auto-selection is handled by the useEffect above

                    return (
                      <>
                        {/* Selection Summary */}
                        <div className="bg-gradient-to-r from-brand-50 to-brand-50 rounded-card p-5 mb-6">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="text-lg font-semibold text-gray-900">
                                {selectedPages.size} of {effectiveLimit || limitedPages.length} Pages Selected
                                {botCategories.length > 0 && (
                                  <span className="text-sm text-brand-600 ml-2">
                                    (for {botPurpose})
                                  </span>
                                )}
                              </h3>
                              {totalDiscoveredPages > preScannedPages.length && (
                                <p className="text-sm text-amber-700 mt-1 font-medium">
                                  {totalDiscoveredPages} pages discovered • Showing {limitedPages.length} relevant pages
                                </p>
                              )}
                              {shouldFilterPages && baseLength < preScannedPages.length && (
                                <p className="text-sm text-brand-700 mt-1 font-medium">
                                  ✅ Bot type filtering applied - showing only relevant pages
                                </p>
                              )}
                              {!shouldFilterPages && botCategories.length > 0 && (
                                <p className="text-sm text-brand-700 mt-1 font-medium">
                                  ℹ️ General assistant selected — including every discovered page
                                </p>
                              )}
                              {botCategories.length === 0 && (
                                <p className="text-sm text-amber-700 mt-1 font-medium">
                                  ⚠️ No bot type recommendations found - showing all pages
                                </p>
                              )}
                              {pagesWithCategories.length === 0 && (
                                <p className="text-sm text-amber-700 mt-1 font-medium">
                                  ⚠️ Pages don't have categories - showing all pages
                                </p>
                              )}
                              <p className="text-sm text-gray-600 mt-1">
                                The AI will learn from the selected pages to answer questions
                              </p>
                            </div>
                            <div className="text-right">
                              <div className="text-2xl font-bold text-brand-600">
                                {(() => {
                                  const denominator = effectiveLimit || limitedPages.length || 1;
                                  return Math.min(100, Math.round((selectedPages.size / denominator) * 100));
                                })()}%
                              </div>
                              <div className="text-sm text-gray-500">
                                {effectiveLimit || limitedPages.length} total pages
                              </div>
                            </div>
                          </div>
                        </div>
                      </>
                    );
                  })()}

                  {/* Select All/Deselect All Button */}
                  <div className="flex justify-center mb-6">
                    <button
                      type="button"
                      onClick={() => {
                        // Find the selected bot recommendation to get total pages
                        const selectedBotRec = botRecommendations.find(rec =>
                          rec.bot_type === botPurpose ||
                          rec.bot_type.toLowerCase().replace(/ /g, '-') === botPurpose ||
                          botPurpose === rec.bot_type.toLowerCase().replace(/ /g, '-')
                        );
                        const botCategories = Array.isArray(selectedBotRec?.category_names) ? selectedBotRec.category_names : [];
                        const shouldFilterPages = shouldFilterByBotCategories(
                          botCategories,
                          botPurpose,
                          selectedBotRec?.bot_type
                        );
                        const filteredPagesBase = shouldFilterPages
                          ? preScannedPages.filter(page => page.category && botCategories.includes(page.category))
                          : preScannedPages;

                        const limit = selectedBotRec?.total_urls
                          ? Math.min(selectedBotRec.total_urls, filteredPagesBase.length, MAX_PAGE_SELECTION)
                          : Math.min(filteredPagesBase.length, MAX_PAGE_SELECTION);
                        const limitedPages = filteredPagesBase.slice(0, limit);
                        const limitedSet = new Set(limitedPages.map((p: any) => p.url));
                        const allSelected = limitedSet.size === selectedPages.size &&
                          Array.from(limitedSet).every(url => selectedPages.has(url));

                        if (allSelected) {
                          setSelectedPages(new Set());
                        } else {
                          setSelectedPages(limitedSet);
                        }
                        setHasManualSelection(true);
                      }}
                      className="px-4 py-2 bg-white border border-brand-200 rounded-lg text-sm font-medium text-brand-700 hover:bg-brand-50 transition-colors"
                    >
                      {(() => {
                        const selectedBotRec = botRecommendations.find(rec =>
                          rec.bot_type === botPurpose ||
                          rec.bot_type.toLowerCase().replace(/ /g, '-') === botPurpose ||
                          botPurpose === rec.bot_type.toLowerCase().replace(/ /g, '-')
                        );
                        const botCategories = Array.isArray(selectedBotRec?.category_names) ? selectedBotRec.category_names : [];
                        const shouldFilterPages = shouldFilterByBotCategories(
                          botCategories,
                          botPurpose,
                          selectedBotRec?.bot_type
                        );
                        const filteredPagesBase = shouldFilterPages
                          ? preScannedPages.filter(page => page.category && botCategories.includes(page.category))
                          : preScannedPages;
                        const limit = selectedBotRec?.total_urls
                          ? Math.min(selectedBotRec.total_urls, filteredPagesBase.length, MAX_PAGE_SELECTION)
                          : Math.min(filteredPagesBase.length, MAX_PAGE_SELECTION);
                        const limitedPages = limit > 0 ? filteredPagesBase.slice(0, limit) : filteredPagesBase;

                        if (!limitedPages.length) return 'Select All';
                        const limitedAllSelected =
                          selectedPages.size === limitedPages.length &&
                          limitedPages.every(page => selectedPages.has(page.url));
                        return limitedAllSelected ? 'Deselect All' : 'Select All';
                      })()}
                    </button>
                  </div>

                {/* Filter pages based on selected bot type */}
                  {(() => {
                    // Find the selected bot recommendation - handle both ID format and bot_type format
                    const selectedBotRec = botRecommendations.find(rec =>
                      rec.bot_type === botPurpose ||
                      rec.bot_type.toLowerCase().replace(/ /g, '-') === botPurpose ||
                      botPurpose === rec.bot_type.toLowerCase().replace(/ /g, '-')
                    );

                    const botCategories = Array.isArray(selectedBotRec?.category_names) ? selectedBotRec.category_names : [];
                    const shouldFilterPages = shouldFilterByBotCategories(
                      botCategories,
                      botPurpose,
                      selectedBotRec?.bot_type
                    );
                    const filteredPagesBase = shouldFilterPages
                      ? preScannedPages.filter(page => page.category && botCategories.includes(page.category))
                      : preScannedPages;
                    const limit = selectedBotRec?.total_urls
                      ? Math.min(selectedBotRec.total_urls, filteredPagesBase.length, MAX_PAGE_SELECTION)
                      : Math.min(filteredPagesBase.length, MAX_PAGE_SELECTION);
                    const limitedPages = limit > 0 ? filteredPagesBase.slice(0, limit) : filteredPagesBase;

                    return (
                      <>
                        {/* Pages List with Better Styling */}
                        <div className="border border-gray-200 rounded-card p-2 max-h-[400px] overflow-y-auto mb-6">
                          <div className="space-y-2">
                            {limitedPages.map((page: any, idx: number) => (
                            <label
                              key={idx}
                              className="flex items-start p-4 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors group"
                            >
                              <input
                                type="checkbox"
                                checked={selectedPages.has(page.url)}
                              onChange={() => {
                                const newSelected = new Set(selectedPages);
                                if (newSelected.has(page.url)) {
                                  newSelected.delete(page.url);
                                } else {
                                  newSelected.add(page.url);
                                }
                                setSelectedPages(newSelected);
                                setHasManualSelection(true);
                              }}
                                className="w-5 h-5 text-brand-600 rounded-lg focus:ring-2 focus:ring-brand-500 mt-1 mr-4"
                              />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between">
                                  <div className="flex-1 pr-2">
                                    <p className="font-medium text-gray-900 group-hover:text-brand-600 transition-colors">
                                      {page.title || 'Untitled Page'}
                                    </p>
                                    <p className="text-sm text-gray-500 mt-1 truncate">
                                      {page.url}
                                    </p>
                                  </div>
                                  {page.category && (
                                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-brand-100 text-brand-800 mt-1">
                                      {page.category}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </label>
                          ))}
                          </div>
                        </div>

                            {/* Show filtered pages info */}
                            {botCategories.length > 0 && (
                                <div className="bg-brand-50 border border-brand-200 rounded-lg p-4 mb-4">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm font-medium text-brand-900">
                                                Showing pages for: <span className="font-semibold">{botPurpose}</span>
                                            </p>
                                            <p className="text-xs text-brand-700 mt-1">
                                                {shouldFilterPages
                                                  ? `Filtered from ${preScannedPages.length} total pages • ${limitedPages.length} pages match selected categories • ${selectedPages.size} selected for crawling`
                                                  : `All ${preScannedPages.length} discovered pages are available • ${selectedPages.size} selected for crawling`}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                      );
                    })()}

                    {/* Action Buttons */}
                    <div className="flex justify-between items-center pt-8 mt-8 border-t border-gray-100">
                    <button
                      type="button"
                      onClick={() => setCurrentStep('categories')}
                      className="flex items-center text-gray-600 hover:text-gray-800 font-medium transition-colors"
                    >
                      <ArrowLeft size={16} className="mr-2 inline" />
                      Back to Bot Selection
                    </button>
                    <button
                      type="button"
                      onClick={handleRescrap}
                      disabled={isRescrapLoading || selectedPages.size === 0}
                      className={`
                        flex items-center px-8 py-3.5 rounded-xl font-semibold text-white
                        transition-all duration-200 transform
                        ${isRescrapLoading || selectedPages.size === 0
                          ? 'bg-gray-400 cursor-not-allowed' 
                          : 'bg-gradient-to-r from-brand-500 to-brand-600 hover:from-brand-600 hover:to-brand-700 hover:scale-105 shadow-lg hover:shadow-card'
                        }
                      `}
                    >
                      {isRescrapLoading ? (
                        <>
                          <Loader2 size={16} className="animate-spin mr-2 inline" />
                          Starting...
                        </>
                      ) : (
                        <>
                          <Bug size={16} className="mr-2 inline" />
                          {(() => {
                            // Calculate total filtered pages for current bot type
                            const selectedBotRec = botRecommendations.find(rec =>
                              rec.bot_type === botPurpose ||
                              rec.bot_type.toLowerCase().replace(/ /g, '-') === botPurpose ||
                              botPurpose === rec.bot_type.toLowerCase().replace(/ /g, '-')
                            );
                            const botCategories = Array.isArray(selectedBotRec?.category_names) ? selectedBotRec.category_names : [];
                            const shouldFilterPages = shouldFilterByBotCategories(
                              botCategories,
                              botPurpose,
                              selectedBotRec?.bot_type
                            );
                            const filteredPagesBase = shouldFilterPages
                              ? preScannedPages.filter(page => page.category && botCategories.includes(page.category))
                              : preScannedPages;
                            const limit = selectedBotRec?.total_urls
                              ? Math.min(selectedBotRec.total_urls, filteredPagesBase.length, MAX_PAGE_SELECTION)
                              : Math.min(filteredPagesBase.length, MAX_PAGE_SELECTION);
                            const totalFilteredPages = limit > 0 ? limit : filteredPagesBase.length;

                            return `Start Crawling ${totalFilteredPages} Page${totalFilteredPages === 1 ? '' : 's'}`;
                          })()}
                        </>
                      )}
                    </button>
                    </div>
                </div>
              </>
            )}
          </div>
          ) : activeTab === 'youtube' ? (
            <div className="-m-8">
              <YouTubeVideos clientId={clientId} />
            </div>
          ) : null}
        </div>
      </div>
    );
  };

export default DocumentUpload; 
